import {
  elapsed,
  elapsedCerrado,
  elapsedMinutos,
  fecha_actual,
} from "../hooks/elapsed";

export interface TiemposProps {
  data: any;
}

export const TiemposComponent = ({ data }: TiemposProps) => {
  if (!data) return null;

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* --- COLUMNA IZQUIERDA: TIMELINE DEL TRÁMITE (2/3) --- */}
        <div className="col-span-1 lg:col-span-2">
          <section className="h-full rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </div>
                Trazabilidad y Tracking Operativo
              </h2>
            </div>

            <div className="p-6">
              {!data.semaforo || data.semaforo.length === 0 ? (
                <div className="py-8 text-center text-sm font-medium text-slate-400">
                  No hay historial de movimientos registrado.
                </div>
              ) : (
                <div className="relative">
                  {data.semaforo.map((s: any, i: number, a: any[]) => {
                    const isLast = i === a.length - 1;

                    // Lógica de cálculo de tiempo
                    let minutos = "0 min";
                    if (!isLast) {
                      const tiempo_old = a[i + 1].ttes_fd_fecha_inicial;
                      minutos = elapsedCerrado(s.ttes_fd_fecha_inicial, tiempo_old);
                    } else if (isLast && s.tce_fi_finalizado === 0) {
                      minutos = elapsed(s.ttes_fd_fecha_inicial);
                    }

                    // Formateo para dividir la fecha y hora si es posible
                    const fechaStr = fecha_actual(s.ttes_fd_fecha_inicial) || "";
                    const [fecha, hora] = fechaStr.includes(" ") ? fechaStr.split(" ") : [fechaStr, ""];

                    return (
                      <div key={s.ttes_id} className="flex gap-4">
                        {/* Fecha y Grupo (Izquierda) */}
                        <div className="w-24 shrink-0 pt-1 text-right">
                          <div className="text-xs font-bold text-slate-700">{fecha}</div>
                          {hora && <div className="text-[10px] text-slate-500">{hora}</div>}
                          <div className="mt-1 text-[9px] font-bold uppercase text-brand-600">
                            {s.tce_fc_grupo}
                          </div>
                        </div>

                        {/* Nodo y Línea conectora (Centro) */}
                        <div className="flex flex-col items-center">
                          <div className={`z-10 h-3.5 w-3.5 rounded-full ring-4 ${isLast ? 'bg-brand-500 ring-brand-100' : 'bg-slate-300 ring-white border border-slate-400'}`}></div>
                          {!isLast && <div className="my-1 w-[2px] grow bg-slate-200"></div>}
                        </div>

                        {/* Contenido de la Etapa (Derecha) */}
                        <div className="pb-8 pt-0.5">
                          <h4 className={`text-sm font-bold leading-tight ${isLast ? 'text-brand-700' : 'text-slate-800'}`}>
                            {s.tce_fc_nombre_etapa}
                          </h4>
                          <p className="mt-0.5 text-xs font-medium text-slate-600">
                            {s.fc_sub_etapa}
                          </p>

                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                              <svg className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                              {s.eje_fc_nombre} {s.eje_fc_apellido_paterno} {s.eje_fc_apellido_materno}
                            </span>

                            <span className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold ${isLast && s.tce_fi_finalizado === 0 ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                              {minutos}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* --- COLUMNA DERECHA: KPIs GLOBALES (1/3) --- */}
        <div className="col-span-1 h-full">
          <section className="flex h-full flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                </div>
                Consolidado de Tiempos (SLA)
              </h2>
            </div>

            <div className="grow p-6">
              {!data.global || data.global.length === 0 ? (
                <div className="py-8 text-center text-sm font-medium text-slate-400">
                  No hay datos globales disponibles.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                  {data.global.map((g: any) => (
                    <div
                      key={g.grupo}
                      className="flex flex-col justify-center rounded-xl border border-slate-200 bg-slate-50/50 p-4 transition-colors hover:border-brand-300 hover:bg-brand-50/30"
                    >
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        {g.grupo}
                      </span>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-xl font-black tracking-tight text-brand-700">
                          {elapsedMinutos(g.tiempo)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

      </div>
    </div>
  );
};
